import React from 'react'
import styled from 'styled-components'
import { color } from '../styles'
import { SPACING } from '../styles/mixins/constants'
import { PageLabel } from './PageLabel'
import { Spacer } from './Spacer'
import { Button } from './Button'

type Props = {
    header: string
    subheader?: string
    children: JSX.Element | JSX.Element[]
    saveLabel?: string
    editLabel?: string
    cancelLabel?: string
    deleteLabel?: string
    outgateLabel?: string
    disableSave?: boolean
    disableCancel?: boolean
    disableDelete?: boolean
    disableOutgate?: boolean
    disableEdit?: boolean
    className?: string
    isDeleting?: boolean
    isOutgating?: boolean
    isSaving?: boolean
    onDelete?: () => void
    onClose: () => void
    onSave?: () => void
    onCancel?: () => void
    onEdit?: () => void
    onOutgate?: () => void
}

export const PanelContainer = (props: Props): JSX.Element => {
    const {
        header,
        subheader,
        children,
        saveLabel = 'Save',
        cancelLabel = 'Cancel',
        deleteLabel = 'Delete',
        editLabel = 'Edit',
        outgateLabel = 'Outgate',
        disableSave,
        disableCancel,
        disableDelete,
        className,
        isSaving,
        isDeleting,
        isOutgating,
        disableEdit,
        disableOutgate,
        onDelete,
        onClose,
        onSave,
        onCancel,
        onEdit,
        onOutgate,
    } = props
    const showFooter = !!onCancel || !!onSave || !!onEdit

    return (
        <Container className={className}>
            <Header>
                <Button onClick={onClose} iconKey="ArrowLeft" type="text" size="large" />
                <Spacer size={4} />
                <PageLabel title={header} description={subheader} />
                {onOutgate && (
                    <>
                        <Spacer size="fill" />
                        <Button
                            type="accent"
                            iconKey="ArrowLeft"
                            size="large"
                            onClick={onOutgate}
                            label={outgateLabel}
                            isLoading={!!isOutgating}
                            style={{
                                display: disableOutgate && 'none',
                            }}
                        />
                    </>
                )}
                <Spacer size={2} />
                {onDelete && (
                    <>
                        <Spacer size="fill" />
                        <Button
                            type="danger"
                            iconKey="Trash"
                            size="large"
                            onClick={onDelete}
                            label={deleteLabel}
                            disabled={disableDelete}
                            isLoading={!!isDeleting}
                            style={{
                                display: disableDelete && 'none',
                            }}
                        />
                    </>
                )}
            </Header>
            <Content>{children}</Content>
            {showFooter ? (
                <Footer>
                    {onCancel && (
                        <Button
                            type="primary"
                            label={cancelLabel}
                            onClick={onCancel}
                            size="large"
                            iconKey="Ex"
                            disabled={Boolean(disableCancel)}
                        />
                    )}
                    <Spacer size={4} />
                    {onSave && (
                        <Button
                            type="accent"
                            label={saveLabel}
                            size="large"
                            iconKey="Check"
                            onClick={onSave}
                            disabled={Boolean(disableSave)}
                            isLoading={Boolean(isSaving)}
                        />
                    )}
                    {onEdit && (
                        <Button
                            type="primary"
                            label={editLabel}
                            onClick={onEdit}
                            size="large"
                            iconKey="Edit"
                            disabled={disableEdit}
                        />
                    )}
                </Footer>
            ) : null}
        </Container>
    )
}

const Container = styled.div`
    background-color: ${color.background.app};
    display: grid;
    grid-row-gap: ${SPACING[4]};
    grid-template-rows: auto 1fr auto;
    width: 100%;
    grid-template-areas:
        'header'
        'content'
        'footer';
`

const Content = styled.div`
    grid-area: content;
    flex-flow: column nowrap;
    display: flex;
    flex: 1 1 auto;
    overflow: hidden;
    min-width: 0;
    min-height: 0;
`
const Header = styled.div`
    grid-area: header;
    display: flex;
    flex-flow: row nowrap;
    align-items: center;
`

const Footer = styled.div`
    grid-area: footer;
    display: flex;
    flex-flow: row nowrap;
    justify-content: flex-end;
`
